<script>
import debounce from 'lodash/debounce';
import map from 'lodash/map';
import Multiselect from 'vue-multiselect';
import PointOfInterestModel from '@models/PointOfInterest';
import LocationModel from '@models/Location';
import BlockingContainer from '@components/BlockingContainer';
import HeaderComponent from '@components/HeaderComponent';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import Layout from '@layouts/main';

export default {
  name: 'PointOfInterestsIndex',
  page() {
    return { title: this.$t('titles.poi') };
  },
  components: {
    FontAwesomeIcon,
    BlockingContainer,
    HeaderComponent,
    Multiselect,
    Layout,
  },
  data() {
    return {
      locations: [],
      focused: false,
      filters: {
        search: null,
        locationIds: [],
      },
      sortBy: 'name',
      sortDesc: false,
      totalRows: null,
      perPage: 20,
      currentPage: 1,
      isBusy: false,
      fieldsTable: [
        { key: 'name', label: this.$t('table_labels.name'), sortable: true },
        { key: 'location', label: this.$t('table_labels.location'), sortable: false },
        { key: 'actions', label: this.$t('table_labels.actions'), sortable: false },
      ],
    };
  },
  computed: {
    sortDir() {
      return this.sortDesc ? '-' : '';
    },
  },
  watch: {
    'filters.locationIds'() {
      this.currentPage = 1;
      if (this.$refs.asyncTable) {
        this.$refs.asyncTable.refresh();
      }
    },
    'filters.search'() {
      this.currentPage = 1;
      if (this.$refs.asyncTable) {
        this.$refs.asyncTable.refresh();
      }
    },
  },
  mounted() {
    this.loadLocations();
  },
  methods: {
    loadLocations() {
      LocationModel.orderBy('name').limit(9999).get().then((response) => {
        const data = response.data || response;
        this.locations = data.map((loc) => ({ id: loc.id, name: loc.name }));
      });
    },
    updateFilter: debounce(function (value) {
      this.filters.search = value;
    }, 500),
    itemsProvider(ctx) {
      let query = PointOfInterestModel
        .include('location')
        .orderBy(this.sortDir + ctx.sortBy)
        .page(this.currentPage)
        .limit(ctx.perPage);

      if (this.filters.search) {
        query = query.where('search', this.filters.search);
      }

      if (this.filters.locationIds && this.filters.locationIds.length > 0) {
        query = query.where('location_ids', map(this.filters.locationIds, 'id').join(','));
      }

      return query
        .get()
        .then((response) => {
          this.totalRows = response.meta.total;
          return response.data || [];
        })
        .catch(() => []);
    },
    downloadFile(item) {
      this.$wait.start(`point of interest ${item.id}`);
      const newWindow = window.open('', '_blank');
      this.$http
        .get(`point-of-interests/${item.id}/file`, { responseType: 'blob' })
        .then((response) => {
          const contentType = response.headers['content-type'] || 'application/octet-stream';
          const url = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
          if (newWindow) {
            newWindow.location.href = url;
          } else {
            const link = document.createElement('a');
            link.href = url;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            document.body.appendChild(link);
            link.click();
            link.remove();
          }
          setTimeout(() => window.URL.revokeObjectURL(url), 60000);
          this.$wait.end(`point of interest ${item.id}`);
        })
        .catch(() => {
          if (newWindow) newWindow.close();
          this.$wait.end(`point of interest ${item.id}`);
        });
    },
    openGoogleMaps(link) {
      window.open(link, '_blank', 'noopener,noreferrer');
    },
  },
};
</script>

<template>
  <Layout>
    <b-row class="mb-4">
      <b-col>
        <header class="d-flex card-header text-white p-0 justify-content-between pr-0 bg-dark">
          <h5 class="p-2-5 mb-0">{{ $t('titles.poi') }}</h5>
        </header>
      </b-col>
    </b-row>

    <!-- Filters -->
    <b-row class="mb-4">
      <b-col sm="12" md="4" lg="4">
        <HeaderComponent>
          <template v-slot:header>
            <span>{{ $t('fields.search') }}</span>
          </template>
        </HeaderComponent>
        <b-input-group>
          <b-form-input :value="filters.search" class="form-control-sm" :placeholder="$t('placeholders.search')" @input="updateFilter" />
          <b-input-group-append>
            <b-btn variant="secondary" size="sm" class="text-white border-gray" :disabled="!filters.search" @click="filters.search = ''">{{ $t('actions.clear') }}</b-btn>
          </b-input-group-append>
        </b-input-group>
      </b-col>
      <b-col sm="12" md="4" lg="4" class="mt-3 mt-md-0">
        <HeaderComponent>
          <template v-slot:header>
            <span>{{ $t('fields.locations') }}</span>
          </template>
        </HeaderComponent>
        <b-input-group>
          <div class="multiselect-container kks-multiselect-container-index form-control form-control-sm p-0" :class="{ focused: focused }">
            <Multiselect
              v-model="filters.locationIds"
              :options="locations"
              :multiple="true"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
              :placeholder="$t('placeholders.locations_multiselect')"
              label="name"
              track-by="id"
              @open="focused = true"
              @close="focused = false"
            >&nbsp;</Multiselect>
          </div>
          <b-input-group-append>
            <b-btn
              variant="secondary"
              size="sm"
              class="text-white border-gray"
              :disabled="!filters.locationIds || !filters.locationIds.length"
              @click="filters.locationIds = []"
            >{{ $t('actions.clear') }}</b-btn>
          </b-input-group-append>
        </b-input-group>
      </b-col>
    </b-row>

    <div class="position-relative">
      <BlockingContainer v-if="$wait.is('point of interest processing') || isBusy" />
      <b-table
        ref="asyncTable"
        small
        striped
        :items="itemsProvider"
        stacked="md"
        :busy.sync="isBusy"
        :current-page="currentPage"
        :per-page="perPage"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :fields="fieldsTable"
        :no-sort-reset="true"
        :show-empty="true"
      >
        <!-- custom sorting icons -->
        <template #head()="{ label, field: { key, sortable } }">
          {{ label }}
          <template v-if="sortable">
            <FontAwesomeIcon v-if="sortBy !== key" :icon="['fas', 'arrows-alt-v']" />
            <FontAwesomeIcon v-else-if="sortDesc" :icon="['fas', 'arrow-down']" />
            <FontAwesomeIcon v-else :icon="['fas', 'arrow-up']" />
          </template>
        </template>

        <template #cell(location)="row">
          {{ row.item.location ? row.item.location.name : '—' }}
        </template>

        <template #cell(actions)="row">
          <div class="d-flex flex-wrap" style="gap: 4px;">
            <b-btn
              v-if="row.item.file_name"
              size="sm"
              variant="outline-success"
              :disabled="$wait.is(`download ${row.item.id}`)"
              @click.stop="downloadFile(row.item)"
            >
              <FontAwesomeIcon v-if="!$wait.is(`download ${row.item.id}`)" :icon="['fas', 'download']" />
              <FontAwesomeIcon v-else :icon="['fas', 'spinner']" pulse />
            </b-btn>

            <!-- Remark toggle -->
            <b-btn
              v-if="row.item.remark"
              size="sm"
              :variant="row.detailsShowing ? 'secondary' : 'outline-secondary'"
              @click.stop="row.toggleDetails"
            >
              <FontAwesomeIcon :icon="['fas', 'sticky-note']" />
            </b-btn>

            <!-- Google Maps -->
            <b-btn
              v-if="row.item.google_maps_link"
              size="sm"
              variant="outline-secondary"
              @click.stop="openGoogleMaps(row.item.google_maps_link)"
            >
              <FontAwesomeIcon :icon="['fas', 'map-marker-alt']" />
            </b-btn>
          </div>
        </template>

        <template #row-details="row">
          <div v-if="row.item.remark" class="mb-2">
            <label>{{ $t('fields.remark') }}</label>
            <b-card>
              <b-card-body class="p-0">{{ row.item.remark }}</b-card-body>
            </b-card>
          </div>
        </template>

        <template #empty>
          <div class="text-center">{{ $t('messages.no_items') }}</div>
        </template>
      </b-table>

      <b-row>
        <b-col sm="12">
          <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" />
        </b-col>
      </b-row>
    </div>

  </Layout>
</template>
